import { useEffect, useRef, useState } from 'react';
import NameTh from './NameTh.jsx';
import { PermitChips, usePermitIndex } from './PermitPanel.jsx';
import { checkCard } from '../lib/permit-flags.js';

export function Modal({ title, sub, onClose, children, wide }) {
  // ปิดด้วยพื้นหลังได้เมื่อ "กดลง" และ "ปล่อย" บนพื้นหลังทั้งคู่เท่านั้น
  // เดิมเช็คแค่ click: ลากเลือกข้อความในช่องแล้วปล่อยเมาส์นอกกรอบ → เบราว์เซอร์ยิง click ที่บรรพบุรุษร่วม
  // ซึ่งก็คือพื้นหลัง → หน้าต่างปิดเอง สิ่งที่กรอกค้างไว้หายหมด (user แจ้ง 26 ต.ค. 69 "กรอกอยู่แล้วปิดไปเอง")
  const downOnBack = useRef(false);
  return (
    <div className="mback"
      onMouseDown={e => { downOnBack.current = e.target === e.currentTarget; }}
      onClick={e => {
        const fromBack = downOnBack.current;
        downOnBack.current = false;
        if (fromBack && e.target === e.currentTarget) onClose();
      }}>
      <div className={`mcard ${wide ? 'wide' : ''}`}>
        <div className="mhead">
          <div>
            <div className="mtitle">{title}</div>
            {sub && <div className="msub">{sub}</div>}
          </div>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>
        <div className="mbody">{children}</div>
      </div>
    </div>
  );
}

// as="div" = ช่องที่ข้างในมีปุ่มมาก่อนช่องกรอก — <label> ส่งทุกคลิกบนพื้นที่ว่างไปที่ปุ่มแรกข้างใน
//   เจอจริง 1 พ.ย. 69: กดพื้นที่ว่าง/ชื่อชิปในช่องวัสดุ = กด ✕ ของชิปแรก วัสดุหายเงียบ ๆ แล้วบันทึกทับ
//   htmlFor = ให้กดคำถามแล้วเคอร์เซอร์เข้าช่องนั้น (ใช้คู่กับ as="div")
export const Fld = ({ label, hint, children, as: Tag = 'label', htmlFor }) => (
  <Tag className="fld">
    {htmlFor ? <label className="flabel" htmlFor={htmlFor}>{label}</label> : <span className="flabel">{label}</span>}
    {children}
    {hint && <span className="fhint">{hint}</span>}
  </Tag>
);

// ประเทศที่นำเข้าบ่อย + ชื่อไทย (คนไม่ต้องรู้รหัส 2 ตัว)
export const COMMON_COUNTRIES = [
  ['JP', 'ญี่ปุ่น'], ['CN', 'จีน'], ['US', 'สหรัฐอเมริกา'], ['VN', 'เวียดนาม'], ['KR', 'เกาหลีใต้'],
  ['TW', 'ไต้หวัน'], ['TH', 'ไทย'], ['DE', 'เยอรมนี'], ['IT', 'อิตาลี'], ['FR', 'ฝรั่งเศส'],
  ['GB', 'สหราชอาณาจักร'], ['IN', 'อินเดีย'], ['ID', 'อินโดนีเซีย'], ['MY', 'มาเลเซีย'], ['SG', 'สิงคโปร์'],
  ['AU', 'ออสเตรเลีย'], ['NZ', 'นิวซีแลนด์'], ['HK', 'ฮ่องกง'], ['CH', 'สวิตเซอร์แลนด์'],
  ['NL', 'เนเธอร์แลนด์'], ['SE', 'สวีเดน'], ['NO', 'นอร์เวย์'], ['ES', 'สเปน'], ['CA', 'แคนาดา'],
];
export function CountrySelect({ value, onChange }) {
  const known = COMMON_COUNTRIES.some(([c]) => c === value);
  const [other, setOther] = useState(!!value && !known);
  if (other) return (
    <div className="cc-other">
      <input maxLength={2} value={value ?? ''} placeholder="รหัส 2 ตัว เช่น BR"
        onChange={e => onChange(e.target.value.toUpperCase())}/>
      <button type="button" className="cc-back" onClick={() => setOther(false)}>เลือกจากรายการ</button>
    </div>
  );
  return (
    <select value={value ?? ''} onChange={e => {
      if (e.target.value === '__other') { setOther(true); onChange(''); } else onChange(e.target.value);
    }}>
      <option value="">— เลือกประเทศ —</option>
      {COMMON_COUNTRIES.map(([c, th]) => <option key={c} value={c}>{th} ({c})</option>)}
      <option value="__other">อื่นๆ (พิมพ์รหัสเอง)…</option>
    </select>
  );
}

// ── สร้างตู้ใหม่ — ป้ายเป็นคำถามภาษาคน + ตัวเลือกบอกผลลัพธ์ ──────────────────
export function CreateShipmentModal({ onClose, onSubmit, suggestCode }) {
  const [f, setF] = useState({
    code: suggestCode, label: '', mode: 'sea', load_type: 'FCL',
    clearance: 'broker', origin_port: '', dest_port: '', eta: '', forwarder: '',
  });
  const [alreadyDone, setAlreadyDone] = useState(false);   // บันทึกย้อนหลัง = ตู้จบแล้ว
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { await onSubmit(f, { alreadyDone }); onClose(); }
    catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };

  return (
    <Modal title="สร้างตู้ใหม่" sub="① ตั้งชื่อตู้ → ② เลือกวิธีขนส่ง → ③ เพิ่ม supplier ทีหลังได้" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="รหัสตู้ (แก้ได้)" hint="รูปแบบ SHP-ปีเดือน-ลำดับ เช่น SHP-2607-01 (ระบบต่อเลขให้เดือนละหลายตู้)">
            <input value={f.code} onChange={set('code')} required/>
          </Fld>
          <Fld label="ตั้งชื่อเรียกเองไหม? (เว้นได้)" hint="เว้นไว้ = ใช้ชื่อ supplier อัตโนมัติ">
            <input value={f.label} onChange={set('label')} placeholder="เช่น Snow Peak · เติมสต๊อก"/>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="ของมาทางไหน?">
            <select value={f.mode} onChange={set('mode')}>
              <option value="sea">ทางเรือ — ถูกกว่า ช้ากว่า (ใช้ B/L)</option>
              <option value="air">ทางเครื่องบิน — เร็ว แพงกว่า (ใช้ AWB)</option>
            </select>
          </Fld>
          <Fld label="ตู้นี้ของเราเจ้าเดียวไหม?">
            <select value={f.load_type} onChange={set('load_type')}>
              <option value="FCL">เต็มตู้เจ้าเดียว (FCL) — 1 consignment</option>
              <option value="LCL">รวมตู้หลาย supplier (LCL) — แยกใบขนต่อเจ้า</option>
            </select>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="ใครเดินพิธีศุลกากร?">
            <select value={f.clearance} onChange={set('clearance')}>
              <option value="broker">จ้าง broker — มีปุ่มส่งชุดเอกสาร (ติด CO gate)</option>
              <option value="self">เดินเอง — แอพยังเตือนเอกสารครบชุด</option>
            </select>
          </Fld>
          <Fld label="Forwarder / broker (เว้นได้)">
            <input value={f.forwarder} onChange={set('forwarder')} placeholder="เช่น TKL"/>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="ต้นทาง (ท่า/เมือง)"><input value={f.origin_port} onChange={set('origin_port')} placeholder="เช่น Tokyo"/></Fld>
          <Fld label="ปลายทาง"><input value={f.dest_port} onChange={set('dest_port')} placeholder="เช่น แหลมฉบัง / สุวรรณภูมิ"/></Fld>
          <Fld label="ของถึงวันไหน? (ETA)" hint="ย้อนหลังได้ — ใส่วันจริงที่ของถึง เช่น ม.ค. ที่ผ่านมา"><input type="date" value={f.eta} onChange={set('eta')}/></Fld>
        </div>
        <label className="fld chk">
          <input type="checkbox" checked={alreadyDone} onChange={e => setAlreadyDone(e.target.checked)}/>
          <span>บันทึกย้อนหลัง — ตู้นี้เข้าโกดังเรียบร้อยแล้ว (จบงาน) → เก็บเข้าแท็บ "เสร็จแล้ว" เลย</span>
        </label>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot">
          <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
          <button className="btn1" disabled={busy}>{busy ? 'กำลังสร้าง…' : 'สร้างตู้'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── เพิ่ม consignment (supplier 1 เจ้า = 1 invoice = 1 ใบขน) ─────────────────
// รายชื่อจากแท็บ "แบรนด์" (supplier_name + ชื่อสะกดอื่น) → datalist กันสะกดชื่อเพี้ยนข้ามตู้
function SupplierList({ names = [] }) {
  return <datalist id="ib-supplier-list">{names.map(n => <option key={n} value={n}/>)}</datalist>;
}

export function AddConsignmentModal({ onClose, onSubmit, supplierNames = [] }) {
  const [f, setF] = useState({
    supplier_name: '', origin_country: '', invoice_no: '', incoterm: 'FOB', currency: 'USD',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await onSubmit({ ...f, origin_country: f.origin_country.toUpperCase() || null });
      onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  return (
    <Modal title="เพิ่มของจาก supplier" sub="1 supplier = 1 invoice = 1 ใบขน — deck เอกสารจะคำนวณให้เอง" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="ของเจ้าไหน?" hint={supplierNames.length ? 'เลือกชื่อเดิมจากรายการ — สะกดตรงกัน ระบบจะรวมตู้/บทเรียนของเจ้านี้ให้ถูก' : undefined}>
            <input value={f.supplier_name} onChange={set('supplier_name')} required placeholder="เช่น Snow Peak" list="ib-supplier-list" autoComplete="off"/>
            <SupplierList names={supplierNames}/>
          </Fld>
          <Fld label="ผลิต/ส่งจากประเทศอะไร?" hint="ใช้ตัดสินว่าต้องมี Form FTA ไหม">
            <CountrySelect value={f.origin_country} onChange={v => setF({ ...f, origin_country: v })}/>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="เลข invoice (เว้นได้ ใส่ทีหลัง)"><input value={f.invoice_no} onChange={set('invoice_no')}/></Fld>
          <Fld label="เงื่อนไขราคา (incoterm)">
            <select value={f.incoterm} onChange={set('incoterm')}>
              <option>FOB</option><option>CIF</option><option>EXW</option><option>DDP</option>
            </select>
          </Fld>
          <Fld label="สกุลเงิน">
            <select value={f.currency} onChange={set('currency')}>
              <option>USD</option><option>JPY</option><option>CNY</option><option>EUR</option><option>THB</option>
            </select>
          </Fld>
        </div>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot">
          <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
          <button className="btn1" disabled={busy}>{busy ? 'กำลังเพิ่ม…' : 'เพิ่ม + คำนวณ deck เอกสาร'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── แก้ข้อมูลตู้ (เผื่อพิมพ์ผิด) ──────────────────────────────────────────────
export function EditShipmentModal({ shipment, hasInsurance = false, onClose, onSubmit, onDelete }) {
  const s = shipment;
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const del = async () => {
    setDelBusy(true); setError(null);
    try { await onDelete(); onClose(); }
    catch (er) { setError(er.message ?? String(er)); setConfirmDel(false); }
    finally { setDelBusy(false); }
  };
  const [f, setF] = useState({
    code: s.code ?? '', label: s.label ?? '', mode: s.mode ?? 'sea',
    load_type: s.load_type ?? 'FCL', clearance: s.clearance ?? 'broker',
    forwarder: s.forwarder ?? '', master_bl: s.master_bl ?? '',
    origin_port: s.origin_port ?? '', dest_port: s.dest_port ?? '',
    etd: s.etd ?? '', eta: s.eta ?? '',
    free_time_days: s.free_time_days ?? '', free_time_ends: s.free_time_ends ?? '',
    storage_per_day: s.storage_per_day ?? '',
  });
  const [insurance, setInsurance] = useState(hasInsurance);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const out = { ...f };
      for (const k of ['etd', 'eta', 'free_time_ends']) if (out[k] === '') out[k] = null;
      for (const k of ['free_time_days', 'storage_per_day']) out[k] = out[k] === '' ? null : Number(out[k]);
      if (out.label === '') out.label = null;
      await onSubmit(out, { modeChanged: out.mode !== s.mode, insurance, insuranceChanged: insurance !== hasInsurance });
      onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  return (
    <Modal title={`แก้ข้อมูลตู้ ${s.code}`} sub="แก้ได้ทุกช่อง เผื่อพิมพ์ผิด — เปลี่ยนวิธีขนส่งระบบจะปรับ AWB/BL ให้เอง" onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="รหัสตู้"><input value={f.code} onChange={set('code')} required/></Fld>
          <Fld label="ชื่อเรียก (เว้นได้)"><input value={f.label} onChange={set('label')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="ขนส่งทาง">
            <select value={f.mode} onChange={set('mode')}>
              <option value="sea">ทางเรือ (BL)</option><option value="air">ทางอากาศ (AWB)</option>
            </select>
          </Fld>
          <Fld label="ชนิดตู้">
            <select value={f.load_type} onChange={set('load_type')}>
              <option value="FCL">FCL เต็มตู้</option><option value="LCL">LCL รวมตู้</option>
            </select>
          </Fld>
          <Fld label="เดินพิธี">
            <select value={f.clearance} onChange={set('clearance')}>
              <option value="broker">จ้าง broker</option><option value="self">เดินเอง</option>
            </select>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="Forwarder / broker"><input value={f.forwarder} onChange={set('forwarder')}/></Fld>
          <Fld label="Master B/L (เว้นได้)"><input value={f.master_bl} onChange={set('master_bl')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="ต้นทาง"><input value={f.origin_port} onChange={set('origin_port')}/></Fld>
          <Fld label="ปลายทาง"><input value={f.dest_port} onChange={set('dest_port')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="ETD (ออกจากต้นทาง)"><input type="date" value={f.etd} onChange={set('etd')}/></Fld>
          <Fld label="ETA (ของถึง)"><input type="date" value={f.eta} onChange={set('eta')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="Free time (วัน)"><input type="number" value={f.free_time_days} onChange={set('free_time_days')}/></Fld>
          <Fld label="Free time หมดวันที่"><input type="date" value={f.free_time_ends} onChange={set('free_time_ends')}/></Fld>
          <Fld label="Storage ฿/วัน (หลังหมด free time)"><input type="number" value={f.storage_per_day} onChange={set('storage_per_day')}/></Fld>
        </div>
        <label className="fld chk">
          <input type="checkbox" checked={insurance} onChange={e => setInsurance(e.target.checked)}/>
          <span>ตู้นี้มีประกันภัยขนส่ง — เพิ่มช่องเอกสาร "ประกันภัยขนส่ง" ให้ (ติ๊กออก = เอาช่องออก)</span>
        </label>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot spread">
          {onDelete && !confirmDel && (
            <button type="button" className="btn-del" onClick={() => setConfirmDel(true)}>ลบตู้นี้</button>
          )}
          {onDelete && confirmDel && (
            <span className="del-confirm">
              ลบตู้ {s.code} + ของ/เอกสารทั้งหมดในตู้? (นำออกจากทุกแท็บ)
              <button type="button" className="btn-del" disabled={delBusy} onClick={del}>{delBusy ? 'กำลังลบ…' : 'ยืนยันลบ'}</button>
              <button type="button" className="btn2 sm" onClick={() => setConfirmDel(false)}>ไม่ลบ</button>
            </span>
          )}
          <span className="mfoot-r">
            <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
            <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : 'บันทึก'}</button>
          </span>
        </div>
      </form>
    </Modal>
  );
}

// ── เปลี่ยนวิธีขนส่ง sea↔air จากหัว Console (กดที่ป้าย AIR/SEA) ──────────────
export function SwitchModeModal({ shipment, onClose, onSubmit }) {
  const from = shipment.mode === 'air' ? 'air' : 'sea';
  const to = from === 'air' ? 'sea' : 'air';
  const TH = { sea: 'ทางเรือ (B/L)', air: 'ทางอากาศ (AWB)' };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    setBusy(true); setError(null);
    try { await onSubmit(to); onClose(); }
    catch (er) { setError(er.message ?? String(er)); setBusy(false); }
  };
  return (
    <Modal title={`เปลี่ยนวิธีขนส่ง — ${shipment.code}`} sub={`${TH[from]} → ${TH[to]}`} onClose={onClose}>
      <div className="permit-hint">
        <b>ระบบจะปรับช่องเอกสารให้เอง:</b> เพิ่มช่อง {to === 'air' ? 'Air Waybill' : 'Bill of Lading'} ให้ทุกเจ้าในตู้ ·
        ช่อง {from === 'air' ? 'AWB' : 'B/L'} เดิมที่ยังไม่มีไฟล์จะถูกเอาออก · ถ้ามีไฟล์แล้วจะเก็บไว้เป็น "ไม่บังคับ" (ไม่หาย) ·
        เปลี่ยนกลับได้ทุกเมื่อ
      </div>
      {error && <div className="ferr">{error}</div>}
      <div className="mfoot">
        <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
        <button type="button" className="btn1" disabled={busy} onClick={go}>{busy ? 'กำลังเปลี่ยน…' : `เปลี่ยนเป็น${TH[to]}`}</button>
      </div>
    </Modal>
  );
}

// ── แก้ข้อมูล consignment (supplier / invoice / วันที่) ──────────────────────
export function EditConsignmentModal({ consignment, onClose, onSubmit, onDelete, supplierNames = [] }) {
  const c = consignment;
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const del = async () => {
    setDelBusy(true); setError(null);
    try { await onDelete(); onClose(); }
    catch (er) { setError(er.message ?? String(er)); setConfirmDel(false); }
    finally { setDelBusy(false); }
  };
  const [f, setF] = useState({
    supplier_name: c.supplier_name ?? '', origin_country: c.origin_country ?? '',
    invoice_no: c.invoice_no ?? '', invoice_date: c.invoice_date ?? '',
    incoterm: c.incoterm ?? 'FOB', currency: c.currency ?? 'USD', house_bl: c.house_bl ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => { const v = e.target.value; setF(prev => ({ ...prev, [k]: v })); };

  // ส่งเฉพาะช่องที่แก้จริง — ช่องที่ไม่ได้แตะต้องไม่ถูกเขียนทับ
  //   (27 ต.ค. 69: หน้า Console ไม่ได้โหลด invoice_date มา หน้าต่างนี้เลยส่งวันที่ว่างกลับไปทุกครั้งที่กดบันทึก
  //    = ลบวันที่ invoice ทิ้งเงียบ ๆ · กันไว้ที่นี่ด้วย เผื่อวันหน้ามีช่องใหม่ที่หน้าโหลดไม่ครบอีก)
  const norm = (k, v) => {
    if (k === 'origin_country') return (v || '').toUpperCase() || null;
    if (k === 'invoice_date') return v || null;
    return v ?? '';
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const out = {};
      for (const k of Object.keys(f)) {
        const now = norm(k, f[k]), before = norm(k, c[k]);
        if (now !== before) out[k] = now;
      }
      if (Object.keys(out).length) {
        await onSubmit(out, { originChanged: 'origin_country' in out });
      }
      onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  return (
    <Modal title={`แก้ข้อมูล ${c.supplier_name}`} sub="เปลี่ยนประเทศต้นทางระบบจะปรับ Form สิทธิ (CO) ให้เอง" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="ชื่อ supplier" hint={supplierNames.length ? 'เลือกชื่อเดิมจากรายการ ถ้าสะกดต่างจากตู้เก่า ระบบจะนับเป็นคนละเจ้า' : undefined}>
            <input value={f.supplier_name} onChange={set('supplier_name')} required list="ib-supplier-list" autoComplete="off"/>
            <SupplierList names={supplierNames}/>
          </Fld>
          <Fld label="ประเทศต้นทาง"><CountrySelect value={f.origin_country} onChange={v => setF(prev => ({ ...prev, origin_country: v }))}/></Fld>
        </div>
        <div className="frow">
          <Fld label="เลข invoice"><input value={f.invoice_no} onChange={set('invoice_no')}/></Fld>
          <Fld label="วันที่ invoice"><input type="date" value={f.invoice_date} onChange={set('invoice_date')}/></Fld>
        </div>
        <div className="frow">
          <Fld label="Incoterm">
            <select value={f.incoterm} onChange={set('incoterm')}>
              <option>FOB</option><option>CIF</option><option>EXW</option><option>DDP</option>
            </select>
          </Fld>
          <Fld label="สกุลเงิน">
            <select value={f.currency} onChange={set('currency')}>
              <option>USD</option><option>JPY</option><option>CNY</option><option>EUR</option><option>THB</option>
            </select>
          </Fld>
          <Fld label="House B/L (เว้นได้)"><input value={f.house_bl} onChange={set('house_bl')}/></Fld>
        </div>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot spread">
          {onDelete && !confirmDel && (
            <button type="button" className="btn-del" onClick={() => setConfirmDel(true)}>ลบ supplier นี้</button>
          )}
          {onDelete && confirmDel && (
            <span className="del-confirm">
              ลบ {c.supplier_name} + เอกสารทั้งหมดของเจ้านี้?
              <button type="button" className="btn-del" disabled={delBusy} onClick={del}>{delBusy ? 'กำลังลบ…' : 'ยืนยันลบ'}</button>
              <button type="button" className="btn2 sm" onClick={() => setConfirmDel(false)}>ไม่ลบ</button>
            </span>
          )}
          <span className="mfoot-r">
            <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
            <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : 'บันทึก'}</button>
          </span>
        </div>
      </form>
    </Modal>
  );
}

// ── เลือกสินค้าเข้าตู้ (หลายรายการ) — ค้น + ติ๊กหลายอัน + ตรวจรายการ + ใส่จำนวน ──
export function AddLineItemModal({ consignment, searchProducts, onSubmit, onClose }) {
  const permit = usePermitIndex();
  const [pmOpen, setPmOpen] = useState({});
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState({});          // id → {product, qty}
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const run = async (t) => {
    setSearching(true);
    try { setResults(await searchProducts(t)); } catch (e) { setError(e.message ?? String(e)); }
    setSearching(false);
  };
  useEffect(() => { run(''); }, []);

  const toggle = (p) => setSel(s => {
    const n = { ...s }; if (n[p.id]) delete n[p.id]; else n[p.id] = { product: p, qty: '1' }; return n;
  });
  const setQty = (id, q) => setSel(s => ({ ...s, [id]: { ...s[id], qty: q } }));
  const chosen = Object.values(sel);

  const submit = async () => {
    if (!chosen.length) return;
    setBusy(true); setError(null);
    try {
      await onSubmit(chosen.map(c => ({
        classification_id: c.product.id,
        // ⚠ ชื่อไทยคือ "ชื่อที่ใช้สำแดง" ที่ขึ้นบนเอกสารส่ง broker — ห้ามสลับเป็นอังกฤษตามหน้าจอ
        // (หน้าจอโชว์อังกฤษเพื่อให้ชี้ตัวของได้ ดูเหตุผลใน src/lib/naming.js)
        description: c.product.name_th || c.product.name_en,
        qty: Number(c.qty) || 1, unit: 'ชิ้น',
      })));
      onClose();
    } catch (e) { setError(e.message ?? String(e)); setBusy(false); }
  };

  return (
    <Modal title={`เลือกสินค้าใส่ใบข้อมูลสินค้า — ${consignment.supplier_name}`}
      sub="ค้นหา ติ๊กเลือกได้หลายรายการ ตรวจดูทางขวา ใส่จำนวน แล้วเพิ่มทีเดียว" onClose={onClose} wide>
      <div className="pick2">
        <div className="pick-left">
          <div className="pd-search">
            <input value={term} onChange={e => setTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && run(term)} placeholder="ค้นสินค้าในคลัง (SKU/ชื่อ/แบรนด์)…"/>
            <button className="btn2" onClick={() => run(term)}>ค้น</button>
          </div>
          <div className="pd-list tall">
            {searching && !results.length ? <div className="jr-empty">กำลังค้น…</div> :
              results.map(p => (
                <label className={`pd-row ${sel[p.id] ? 'on' : ''}`} key={p.id}>
                  <input type="checkbox" checked={!!sel[p.id]} onChange={() => toggle(p)}/>
                  <span className="pd-sku">{p.sku || '—'}</span>
                  <span className="pd-nm"><NameTh c={p} missing/></span>
                  <span className="pd-br">{p.hs_code || p.brand || ''}</span>
                </label>
              ))}
            {!searching && !results.length && <div className="jr-empty">ไม่พบสินค้า — ดึงจาก Product Manager หรือเพิ่มในแท็บ "สินค้า & HS" ก่อน</div>}
          </div>
          <div className="sg-hint">พิมพ์คำค้นเพื่อกรองจากสินค้าหลายพันรายการ — ติ๊กทีละหลายอันได้</div>
        </div>
        <div className="pick-right">
          <div className="pick-r-h">เลือกไว้ {chosen.length} รายการ</div>
          {permit.status === 'failed' && (
            <div className="pmw-s">ตรวจเรื่องใบอนุญาตไม่ได้ · <button type="button" className="pm-toggle" onClick={permit.retry}>ลองอีกครั้ง</button></div>
          )}
          <div className="pick-review">
            {chosen.length ? chosen.map(c => (
              <div className="rev-row" key={c.product.id}>
                <span className="rev-nm"><NameTh c={c.product}/></span>
                <input className="rev-qty" type="number" min="0" step="any" value={c.qty}
                  onChange={e => setQty(c.product.id, e.target.value)}/>
                <button className="trayx" onClick={() => toggle(c.product)}>✕</button>
                {/* ⚠ ไว้ทางขวาเท่านั้น ห้ามใส่ใน label.pd-row ทางซ้าย: label ส่งคลิกไปปุ่มแรก (บั๊กชิปหาย 1 พ.ย.) */}
                <div className="rev-pm">
                  <PermitChips result={permit.index ? checkCard(c.product, permit.index) : null} open={!!pmOpen[c.product.id]}
                    onToggle={() => setPmOpen(o => ({ ...o, [c.product.id]: !o[c.product.id] }))}/>
                </div>
              </div>
            )) : <div className="jr-empty">ยังไม่ได้เลือก — ติ๊กสินค้าทางซ้าย</div>}
          </div>
        </div>
      </div>
      {error && <div className="ferr">{error}</div>}
      <div className="mfoot">
        <button type="button" className="btn2" onClick={onClose}>ปิด</button>
        <button className="btn1" disabled={busy || !chosen.length} onClick={submit}>
          {busy ? 'เพิ่ม…' : `เพิ่ม ${chosen.length} รายการเข้าตู้`}
        </button>
      </div>
    </Modal>
  );
}

// ── ตรวจ CO / Form สิทธิ (HARD GATE — ต้องติ๊กครบ; จุดที่เคยพลาดคือ origin criteria) ──
const CO_CHECKS = [
  { id: 'criteria', t: 'Origin Criteria ถูกต้องครบถ้วน', star: true },
  { id: 'hs', t: 'พิกัด HS ตรงกับ invoice / product master' },
  { id: 'value', t: 'มูลค่าตรงกับ invoice' },
  { id: 'names', t: 'ชื่อ-ที่อยู่ exporter / consignee ตรงกับ invoice' },
  { id: 'dates', t: 'วันที่ออก / เลขที่ Form ครบถ้วน' },
];

export function VerifyCOModal({ consignment, onClose, onSubmit }) {
  const [checks, setChecks] = useState({});
  const [f, setF] = useState({ formType: 'D', referenceNo: '', originCriteria: '', issuedDate: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const allChecked = CO_CHECKS.every(c => checks[c.id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); if (!allChecked) return;
    setBusy(true); setError(null);
    try {
      await onSubmit({ ...f, notes: `${f.notes} [checklist ครบ ${CO_CHECKS.length}/${CO_CHECKS.length}]`.trim() });
      onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  return (
    <Modal title={`ตรวจ Form สิทธิ — ${consignment.supplier_name}`}
      sub="ติ๊กครบทุกข้อถึงจะยืนยันได้ — Form ผิดคือเคสที่เคยโดนประเมินย้อนหลัง" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="Form อะไร?">
            <select value={f.formType} onChange={set('formType')}>
              <option value="D">Form D (ASEAN)</option>
              <option value="E">Form E (จีน)</option>
              <option value="AJ_JTEPA">JTEPA / AJCEP (ญี่ปุ่น)</option>
              <option value="AK">Form AK (เกาหลี)</option>
              <option value="AANZ">Form AANZ (ออสเตรเลีย/นิวซีแลนด์)</option>
              <option value="AI">Form AI (อินเดีย)</option>
              <option value="RCEP">RCEP</option>
              <option value="other">อื่นๆ</option>
            </select>
          </Fld>
          <Fld label="เลขที่ Form"><input value={f.referenceNo} onChange={set('referenceNo')} required/></Fld>
          <Fld label="วันที่ออก"><input type="date" value={f.issuedDate} onChange={set('issuedDate')}/></Fld>
        </div>
        <Fld label="Origin Criteria ที่ระบุใน Form" hint="เช่น WO / PE / RVC 40% / CTH — จุดที่เคยพลาด ดูให้ตรงกับสินค้าจริง">
          <input value={f.originCriteria} onChange={set('originCriteria')} required placeholder="เช่น RVC 40%"/>
        </Fld>
        <div className="cochecks">
          {CO_CHECKS.map(c => (
            <label key={c.id} className={`cocheck ${checks[c.id] ? 'on' : ''}`}>
              <input type="checkbox" checked={!!checks[c.id]}
                onChange={e => setChecks({ ...checks, [c.id]: e.target.checked })}/>
              <span>{c.t}{c.star && <b className="costar"> ★ จุดที่เคยพลาด</b>}</span>
            </label>
          ))}
        </div>
        <Fld label="บันทึกเพิ่มเติม (เว้นได้)"><input value={f.notes} onChange={set('notes')}/></Fld>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot">
          <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
          <button className="btn1" disabled={busy || !allChecked}>
            {allChecked ? (busy ? 'กำลังบันทึก…' : 'ยืนยันผ่านการตรวจ') : `ติ๊กให้ครบ (${CO_CHECKS.filter(c => checks[c.id]).length}/${CO_CHECKS.length})`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
